
// vue
import {
  ref,
  reactive,
  defineComponent,
  onUnmounted,
  computed,
  watch,
} from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';

// firebase
import 'firebase/firestore';
import {
  db,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
  fieldDelete,
} from '@/main';

// atoms
import Card from '@/components/atoms/Card.vue';
import CustomButton from '@/components/atoms/CustomButton.vue';
import Divider from '@/components/atoms/Divider.vue';
import Heading from '@/components/atoms/Heading.vue';
import Loader from '@/components/atoms/Loader.vue';
import ProgressBar from '@/components/atoms/ProgressBar.vue';
import TextField from '@/components/atoms/TextField.vue';
import MultilineField from '@/components/atoms/MultilineField.vue';
// molecules
import HeaderMenu from '@/components/molecules/HeaderMenu.vue';
// organisms
import ChatArea from '@/components/organisms/ChatArea.vue';
import ExpiredScreen from '@/components/organisms/ExpiredScreen.vue';

// functions
import {
  loadCurQuestionId,
  loadCompQuestions,
} from '@/functions/pages/Student';

// utils
import {
  calcPercentage,
  getRoomId,
  isExpired,
  isNotContainRoomInfo,
  initObj,
  // Divider
  leftWidth,
  changeWidth,
} from '@/utils';

// types
import { RoomDoc } from '@/types';

export default defineComponent({
  name: 'Student',
  components: {
    // atoms
    Card,
    CustomButton,
    Divider,
    Heading,
    Loader,
    ProgressBar,
    TextField,
    MultilineField,
    // molecules
    HeaderMenu,
    // organisms
    ChatArea,
    ExpiredScreen,
  },
  setup() {
    const route = useRoute();
    const store = useStore();
    const user = computed(() => store.getters.getUser);

    const id = getRoomId(route);
    const curQuestionId = ref(loadCurQuestionId(id));
    store.commit('setCurQuestionId', curQuestionId.value);
    const completeQuestions = reactive<string[]>(loadCompQuestions(id)); // 完了した問題一覧
    const completeVal = computed(() =>
      calcPercentage(
        roomObj?.questionIdList?.length || 0,
        completeQuestions?.length || 0
      )
    );

    const roomObj = reactive({
      roomTitle: '',
      expirationDate: 0,
      studentNum: 0,
    }) as RoomDoc;
    const showExpiredScreen = ref(false);

    /**
     * 現在の回答済みIDよりも後に追加された未回答の問題がある場合は、
     * その問題を現在の問題IDにする
     */
    const adjustCurQuestionId = (
      completeQuestions: string[],
      questionIdList: string[]
    ) => {
      const lastCompQuestion = completeQuestions[completeQuestions.length - 1];
      // 最後まで回答していない場合は終了
      if (curQuestionId.value !== lastCompQuestion) return;

      const curIndex = questionIdList.indexOf(curQuestionId.value);
      // 最後まで回答しているが、次の問題が存在しない場合は終了
      if (curIndex === questionIdList.length - 1) return;

      // 最後まで回答しており、次の問題が存在する場合は次の問題を表示
      curQuestionId.value = questionIdList[curIndex + 1];
      store.commit('setCurQuestionId', curQuestionId.value);
    };

    const isLoading = ref(true);
    const fetchRoom = async () => {
      try {
        await db
          .collection('Room')
          .doc(id)
          .onSnapshot((doc) => {
            const data = doc.data() as RoomDoc;
            roomObj.roomTitle = data?.roomTitle;
            roomObj.expirationDate = data?.expirationDate;
            roomObj.questionIdList = data?.questionIdList;
            roomObj.createdAt = data?.createdAt;
            roomObj.updatedAt = data?.updatedAt;

            // ルーム期限チェック
            // ====================================
            const createdAt = roomObj?.createdAt?.toDate();
            const expirationDate = roomObj?.expirationDate;
            if (!createdAt || isExpired(createdAt, expirationDate)) {
              showExpiredScreen.value = true;
              return;
            }

            // キャッシュした問題が削除された場合はキャッシュからも削除
            for (let i = 0; i < completeQuestions.length; i++) {
              const questionId = completeQuestions[i];

              // DBから取得した問題一覧にローカルでの回答済みの問題IDがない場合
              // → 問題を削除された場合
              if (!roomObj.questionIdList.includes(questionId)) {
                // ローカルから削除された問題を取り除き、
                // 一番最後に回答した問題IDを現在の問題IDにする
                completeQuestions.splice(i, 1);

                if (completeQuestions.length === 0) {
                  curQuestionId.value = '';
                } else {
                  curQuestionId.value =
                    completeQuestions[completeQuestions.length - 1];
                }
                store.commit('setCurQuestionId', curQuestionId.value);
                localStorage.setItem(`curQuestionId${id}`, curQuestionId.value);
                localStorage.setItem(
                  `completeQuestions${id}`,
                  JSON.stringify(completeQuestions)
                );
              }
            }

            // 現在の回答済みIDよりも後に追加された未回答の問題がある場合は、
            // その問題を現在の問題IDにする
            adjustCurQuestionId(completeQuestions, roomObj.questionIdList);

            // データ更新前に全ての問題を解き終わっているか確認
            const totalQuestionNum = roomObj?.questionIdList?.length;
            const compQuestionNum = completeQuestions?.length;
            const percentage = calcPercentage(
              totalQuestionNum,
              compQuestionNum
            );

            // 全て回答済みの状態で新しく問題が追加された場合は問題を進める
            if (percentage === 100 && totalQuestionNum > compQuestionNum) {
              const curQID = roomObj.questionIdList[compQuestionNum];
              curQuestionId.value = curQID;
              localStorage.setItem(`curQuestionId${id}`, curQID);
              store.commit('setCurQuestionId', curQuestionId.value);
            }

            // 値がなかったら初期化
            if (!curQuestionId.value) {
              curQuestionId.value = roomObj.questionIdList[0];
              store.commit('setCurQuestionId', curQuestionId.value);
            }
          });
      } catch (e) {
        // 必要な情報が取得できていない場合は利用不可にする
        if (isNotContainRoomInfo(roomObj)) {
          showExpiredScreen.value = true;
        }
        console.error(e);
      }
      isLoading.value = false;
    };

    const isRoomFetched = ref(false);
    if (user?.value?.uid) {
      isRoomFetched.value = true;
      fetchRoom();
    } else {
      watch(user, () => {
        if (!user?.value?.uid || isRoomFetched.value) return;
        fetchRoom();
      });
    }

    // 問題一覧の初期値を取得（キャッシュor空）
    const questionObj = reactive(initObj(id, 'questionObj'));
    const lastFetchQuestionDate = new Date(2020, 1, 1); //TODO: キャッシュがあればry
    // const lastFetchQuestionDate = initLastFetchDate(
    //   id,
    //   'lastFetchQuestionDate'
    // );
    const questionsUnsubscribe = ref<any>(null);
    const isQuestionFetched = ref(false);
    const fetchQuestions = () => {
      questionsUnsubscribe.value = db
        .collection('Room')
        .doc(id)
        .collection('questions')
        .where('updatedAt', '>', lastFetchQuestionDate)
        .orderBy('updatedAt')
        .onSnapshot((snapShot) => {
          sessionStorage.setItem(
            `lastFetchQuestionDate${id}`,
            String(new Date())
          );
          snapShot.forEach((doc) => {
            const questionId = doc.id;
            questionObj[questionId] = doc.data();
          });

          if (curQuestionId.value === '') {
            curQuestionId.value = roomObj.questionIdList[0];
            store.commit('setCurQuestionId', curQuestionId.value);
          }
          sessionStorage.setItem(
            `questionObj${id}`,
            JSON.stringify(questionObj)
          );
        });
    };
    if (user?.value?.uid) {
      isQuestionFetched.value = true;
      fetchQuestions();
    } else {
      watch(user, () => {
        if (!user?.value?.uid || isQuestionFetched.value) return;
        fetchQuestions();
      });
    }

    const isSendingCompleteQuestion = ref(false);
    const completeQuestion = () => {
      if (isSendingCompleteQuestion.value) return;
      isSendingCompleteQuestion.value = true;
      const totalQuestionNum = roomObj?.questionIdList?.length;
      const compQuestionNum = completeQuestions?.length;
      const percentage = calcPercentage(totalQuestionNum, compQuestionNum);

      // 問題を全て完了しているorまだ解ける問題がない場合は押しても何もしない
      if (percentage === 100 || totalQuestionNum === 0) return;
      // userIdが取得できない場合はDBアクセスしない
      const userId = user?.value?.uid;
      if (!userId) return;

      db.collection('Room')
        .doc(id)
        .collection('questions')
        .doc(curQuestionId.value)
        .set(
          {
            completedUsers: arrayUnion(userId),
            completedUserInfo: {
              [userId]: {
                timeStamp: new Date(),
              },
            },
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        )
        .then(() => {
          completeQuestions.push(curQuestionId.value);
          // 現在の回答済みIDよりも後に追加された未回答の問題がある場合は、
          // その問題を現在の問題IDにする
          adjustCurQuestionId(completeQuestions, roomObj.questionIdList);

          store.commit('setCurQuestionId', curQuestionId.value);
          localStorage.setItem(`curQuestionId${id}`, curQuestionId.value);
          localStorage.setItem(
            `completeQuestions${id}`,
            JSON.stringify(completeQuestions)
          );
        })
        .catch((e) => {
          console.error(e);
          alert(
            'リクエストの送信に失敗しました。ブラウザをリロード後、再度実行をお願いします。'
          );
        })
        .finally(() => {
          isSendingCompleteQuestion.value = false;
        });
    };

    const isSendingUndoQuestion = ref(false);
    const undoQuestion = async () => {
      if (completeQuestions.length === 0) return;
      if (isSendingUndoQuestion.value) return;
      isSendingUndoQuestion.value = true;

      const lastIndex = completeQuestions.length - 1;
      // completeQuestionIdから最後ののIDを消す
      const deleteQuestionId = completeQuestions.splice(lastIndex, 1);
      // curQuestionIdを１つ前にずらす
      curQuestionId.value = roomObj.questionIdList[lastIndex];
      // DBの完了から削除する
      const userId = user?.value?.uid;
      const deleteFieldName = `completedUserInfo.${userId}`;
      try {
        await db
          .collection('Room')
          .doc(id)
          .collection('questions')
          .doc(curQuestionId.value)
          .update({
            [deleteFieldName]: fieldDelete(),
            completedUsers: arrayRemove(userId),
          });

        store.commit('setCurQuestionId', curQuestionId.value);
        localStorage.setItem(`curQuestionId${id}`, curQuestionId.value);
        localStorage.setItem(
          `completeQuestions${id}`,
          JSON.stringify(completeQuestions)
        );
      } catch (e) {
        console.error(e);
        alert('戻るに失敗しました。ブラウザをリロードして再度お試しください。');
        completeQuestions.push(deleteQuestionId[0]);
        curQuestionId.value = roomObj.questionIdList[lastIndex];
      }

      isSendingUndoQuestion.value = false;
    };

    onUnmounted(() => {
      questionsUnsubscribe.value();
    });

    return {
      isLoading,
      user,
      id,
      roomObj,
      questionObj,
      curQuestionId,
      completeQuestions,
      completeQuestion,
      undoQuestion,
      completeVal,
      calcPercentage,
      // 期限切れの表示用
      showExpiredScreen,
      // コンテンツの横幅
      leftWidth,
      changeWidth,
    };
  },
});
